import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Crown,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  Loader2,
  MessageSquare,
  FileText,
  HardDrive,
  Brain,
  Image,
  Activity,
  Calendar,
  Cpu,
  BookOpen,
  Clock,
  Hash,
  Download,
  Upload,
} from 'lucide-react';
import { useTierList, useTierSchema, useCreateTier, useUpdateTier, useDeleteTier } from '../hooks';
import { Tier, TierConfig, TierCreateRequest, TierUpdateRequest, tiersApi } from '../api';
import { getValidationErrors } from '../utils/error';

interface TierFormData {
  id: string;
  config: TierConfig;
}

const inputClass =
  'w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 font-mono text-sm';

/** Model picker — a select over agent-server's model list, free text if the list is unavailable. */
const ModelInput: React.FC<{
  value: string;
  options: string[] | null;
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => {
  if (!options) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="예: gemini-3.1-flash-lite"
        className={inputClass}
      />
    );
  }
  // A stored ID that left the list stays visible so saving does not silently change it.
  const choices = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
      {!value && <option value="">선택</option>}
      {choices.map((id) => (
        <option key={id} value={id}>
          {options.includes(id) ? id : `${id} (목록에 없음)`}
        </option>
      ))}
    </select>
  );
};

const defaultConfig: TierConfig = {
  name: '',
  llm_model: 'gemini-3.1-flash-lite',
  task_model: 'gemini-3.1-flash-lite',
  llm_thinking_budget: 1024,
  task_thinking_budget: 0,
  max_storage_bytes: 10485760,
  max_documents: 10,
  max_file_size_bytes: 10485760,
  daily_chat_limit: 15,
  daily_analysis_limit: 3,
  analysis_memory_count: 10,
  max_memories: 10,
  memory_retention_days: 7,
  memory_recent_count: 10,
  memory_search_count: 5,
  monthly_multimodal_limit: 3,
  summary_min_turns: 12,
  summary_keep_turns: 3,
  summary_max_sentences: 10,
  summary_max_tokens: 60000,
  session_idle_hours: 4,
};

const Tiers: React.FC = () => {
  const { t } = useTranslation();
  const { data: tierData, isLoading } = useTierList();
  const { data: schemaData } = useTierSchema();
  const modelOptions = (key: string) => schemaData?.fields.find((f) => f.key === key)?.options ?? null;
  const createTier = useCreateTier();
  const updateTier = useUpdateTier();
  const deleteTier = useDeleteTier();

  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [formData, setFormData] = useState<TierFormData>({ id: '', config: { ...defaultConfig } });
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  const tiers = tierData?.tiers || [];

  const handleExport = () => {
    if (!tiers.length) return;
    const data = {
      exported_at: new Date().toISOString(),
      tiers: tiers.map((t) => ({ id: t.id, config: t.config })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tiers-config-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setImporting(true);
    setImportResult(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (
        !Array.isArray(data?.tiers) ||
        data.tiers.some((t: { id?: string; config?: unknown }) => !t.id || !t.config)
      ) {
        setImportResult('Invalid file format');
        setImporting(false);
        return;
      }

      let created = 0,
        updated = 0,
        failed = 0;
      const errors: string[] = [];

      for (const tier of data.tiers) {
        try {
          const existing = tiers.find((t) => t.id === tier.id);
          if (existing) {
            await tiersApi.update(tier.id, { config: tier.config });
            updated++;
          } else {
            await tiersApi.create({ id: tier.id, config: tier.config });
            created++;
          }
        } catch (err: unknown) {
          failed++;
          const validationErrors = getValidationErrors(err);
          if (validationErrors) {
            errors.push(`${tier.id}: ${validationErrors.join(', ')}`);
          } else {
            errors.push(`${tier.id}: Unknown error`);
          }
        }
      }

      const parts: string[] = [];
      if (created) parts.push(`${created} created`);
      if (updated) parts.push(`${updated} updated`);
      if (failed) parts.push(`${failed} failed`);
      setImportResult(parts.join(', ') + (errors.length ? `\n${errors.join('\n')}` : ''));

      // Refresh tier list
      if (created || updated) {
        window.location.reload();
      }
    } catch {
      setImportResult('Failed to parse JSON file');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (!showModal) {
      setFormData({ id: '', config: { ...defaultConfig } });
      setEditingTier(null);
    }
  }, [showModal]);

  const handleCreate = () => {
    setEditingTier(null);
    setFormData({ id: '', config: { ...defaultConfig } });
    setShowModal(true);
  };

  const handleEdit = (tier: Tier) => {
    setEditingTier(tier);
    setFormData({
      id: tier.id,
      config: { ...defaultConfig, ...tier.config },
    });
    setShowModal(true);
  };

  const handleDelete = async (tierId: string) => {
    try {
      await deleteTier.mutateAsync(tierId);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete tier:', error);
      alert('Cannot delete default tiers (free, basic, pro, lifetime)');
    }
  };

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);

    try {
      if (editingTier) {
        const updateData: TierUpdateRequest = {
          config: formData.config,
        };
        await updateTier.mutateAsync({ tierId: editingTier.id, data: updateData });
      } else {
        const createData: TierCreateRequest = {
          id: formData.id,
          config: formData.config,
        };
        await createTier.mutateAsync(createData);
      }
      setShowModal(false);
    } catch (error: unknown) {
      // Extract validation errors from 422 response
      const errors = getValidationErrors(error);
      if (errors) {
        setValidationErrors(errors);
      } else {
        console.error('Failed to save tier:', error);
      }
    }
  };

  // Byte field unit state: track selected unit per field
  const [byteUnits, setByteUnits] = useState<Record<string, 'MB' | 'GB'>>({});

  const getByteUnit = (key: string, bytes: number): 'MB' | 'GB' => {
    if (byteUnits[key]) return byteUnits[key];
    return bytes >= 1024 * 1024 * 1024 ? 'GB' : 'MB';
  };

  const bytesToDisplay = (bytes: number, unit: 'MB' | 'GB'): number => {
    const divisor = unit === 'GB' ? 1024 * 1024 * 1024 : 1024 * 1024;
    return Math.round((bytes / divisor) * 100) / 100;
  };

  const displayToBytes = (value: number, unit: 'MB' | 'GB'): number => {
    const multiplier = unit === 'GB' ? 1024 * 1024 * 1024 : 1024 * 1024;
    return Math.round(value * multiplier);
  };

  const updateConfig = (key: keyof TierConfig, value: unknown) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value },
    });
  };

  const getTierColor = (tierId: string) => {
    switch (tierId?.toLowerCase()) {
      case 'pro':
        return 'from-purple-500 to-purple-600';
      case 'basic':
        return 'from-blue-500 to-blue-600';
      case 'lifetime':
        return 'from-amber-500 to-amber-600';
      case 'enterprise':
        return 'from-emerald-500 to-emerald-600';
      default:
        return 'from-slate-400 to-slate-500';
    }
  };

  const formatBytes = (bytes: number | undefined): string => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const formatLimit = (limit: number | null | undefined): string => {
    if (limit === null || limit === undefined) return '무제한';
    return limit.toLocaleString();
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('tiers.title')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('tiers.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={tiers.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <Download size={16} />
            Export
          </button>
          <button
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Import
          </button>
          <input ref={importInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            {t('tiers.newTier')}
          </button>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <div
          className={`p-3 rounded-lg text-sm flex items-center justify-between ${
            importResult.includes('failed') || importResult.includes('Invalid') || importResult.includes('Failed')
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}
        >
          <pre className="whitespace-pre-wrap">{importResult}</pre>
          <button onClick={() => setImportResult(null)} className="ml-2 hover:opacity-70">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tier Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin mx-auto mb-2 text-blue-500" />
            <p className="text-slate-500">{t('common.loading')}</p>
          </div>
        </div>
      ) : tiers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Crown size={48} className="mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">{t('tiers.noTiers')}</h3>
          <p className="text-slate-500 mb-6">{t('tiers.createFirst')}</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            {t('tiers.newTier')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Tier Header */}
              <div className={`bg-gradient-to-r ${getTierColor(tier.id)} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Crown size={20} />
                    <h3 className="font-bold text-lg uppercase">{tier.id}</h3>
                  </div>
                  {tier.config.name && <span className="text-sm text-white/80">{tier.config.name}</span>}
                </div>
              </div>

              {/* Tier Content */}
              <div className="p-4 space-y-4">
                {/* LLM Models */}
                <div className="space-y-1.5">
                  {tier.config.llm_model && (
                    <div className="flex items-center gap-2 text-sm">
                      <Cpu size={14} className="text-slate-400" />
                      <span className="text-xs text-slate-500">Chat</span>
                      <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-700">
                        {tier.config.llm_model}
                      </span>
                    </div>
                  )}
                  {tier.config.task_model && (
                    <div className="flex items-center gap-2 text-sm">
                      <Cpu size={14} className="text-slate-400" />
                      <span className="text-xs text-slate-500">Task</span>
                      <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-700">
                        {tier.config.task_model}
                      </span>
                    </div>
                  )}
                </div>

                {/* Limits */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">{t('tiers.limits')}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <MessageSquare size={14} />
                        <span>일일 채팅</span>
                      </div>
                      <span className="font-medium text-slate-800">{formatLimit(tier.config.daily_chat_limit)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Activity size={14} />
                        <span>일일 분석</span>
                      </div>
                      <span className="font-medium text-slate-800">
                        {formatLimit(tier.config.daily_analysis_limit)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <FileText size={14} />
                        <span>문서 수</span>
                      </div>
                      <span className="font-medium text-slate-800">{formatLimit(tier.config.max_documents)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <HardDrive size={14} />
                        <span>저장용량</span>
                      </div>
                      <span className="font-medium text-slate-800">{formatBytes(tier.config.max_storage_bytes)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Brain size={14} />
                        <span>메모리 수</span>
                      </div>
                      <span className="font-medium text-slate-800">{formatLimit(tier.config.max_memories)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Brain size={14} />
                        <span>최근 기억 주입</span>
                      </div>
                      <span className="font-medium text-slate-800">{formatLimit(tier.config.memory_recent_count)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Brain size={14} />
                        <span>검색 기억 주입</span>
                      </div>
                      <span className="font-medium text-slate-800">{formatLimit(tier.config.memory_search_count)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Image size={14} />
                        <span>월간 멀티모달</span>
                      </div>
                      <span className="font-medium text-slate-800">
                        {formatLimit(tier.config.monthly_multimodal_limit)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Summary & Memory Triggers */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">요약 / 기억 추출</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <BookOpen size={14} />
                        <span>최소 턴 수</span>
                      </div>
                      <span className="font-medium text-slate-800">{formatLimit(tier.config.summary_min_turns)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <MessageSquare size={14} />
                        <span>요약 후 유지 턴</span>
                      </div>
                      <span className="font-medium text-slate-800">{tier.config.summary_keep_turns ?? '-'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Hash size={14} />
                        <span>요약 최대 문장 수</span>
                      </div>
                      <span className="font-medium text-slate-800">{tier.config.summary_max_sentences ?? '-'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Activity size={14} />
                        <span>토큰 안전장치</span>
                      </div>
                      <span className="font-medium text-slate-800">
                        {tier.config.summary_max_tokens?.toLocaleString() ?? '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock size={14} />
                        <span>세션 유휴 시간</span>
                      </div>
                      <span className="font-medium text-slate-800">{tier.config.session_idle_hours ?? '-'}h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Brain size={14} />
                        <span>분석 시 기억 참조 수</span>
                      </div>
                      <span className="font-medium text-slate-800">
                        {formatLimit(tier.config.analysis_memory_count)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Calendar size={12} />
                  <span>
                    {tier.valid_from || tier.valid_until
                      ? `${formatDate(tier.valid_from)} ~ ${formatDate(tier.valid_until)}`
                      : '무기한'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 border-t border-slate-100 flex gap-2">
                <button
                  onClick={() => handleEdit(tier)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm"
                >
                  <Edit2 size={14} />
                  {t('common.edit')}
                </button>
                {deleteConfirmId === tier.id ? (
                  <div className="flex-1 flex gap-1">
                    <button
                      onClick={() => handleDelete(tier.id)}
                      disabled={deleteTier.isPending}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50"
                    >
                      {deleteTier.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(null)}
                      className="flex-1 flex items-center justify-center px-3 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirmId(tier.id)}
                    className="flex items-center justify-center gap-2 px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-slate-800">
                {editingTier ? t('tiers.editTier') : t('tiers.createTier')}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-500"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tier ID *</label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                    placeholder={t('tiers.tierNamePlaceholder')}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 disabled:bg-slate-50"
                    required
                    disabled={!!editingTier}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    표시 이름 <span className="font-mono text-xs text-slate-400">(name)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.config.name || ''}
                    onChange={(e) => updateConfig('name', e.target.value)}
                    placeholder="예: Free Plan, Basic Plan"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    LLM 모델 (채팅) <span className="font-mono text-xs text-slate-400">(llm_model)</span>
                  </label>
                  <ModelInput
                    value={formData.config.llm_model || ''}
                    options={modelOptions('llm_model')}
                    onChange={(v) => updateConfig('llm_model', v)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Task 모델 (요약/기억 추출) <span className="font-mono text-xs text-slate-400">(task_model)</span>
                  </label>
                  <ModelInput
                    value={formData.config.task_model || ''}
                    options={modelOptions('task_model')}
                    onChange={(v) => updateConfig('task_model', v)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Chat thinking budget <span className="font-mono text-xs text-slate-400">(llm_thinking_budget)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.config.llm_thinking_budget ?? ''}
                    onChange={(e) =>
                      updateConfig(
                        'llm_thinking_budget',
                        e.target.value === '' ? null : parseInt(e.target.value),
                      )
                    }
                    placeholder="기본 1024. -1=dynamic, 0=off"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Gemini: thinking 토큰 수(목표). GPT: reasoning effort 로 바뀜 — 0→none, 2048 이하→low, 그 위→medium, -1→모델 기본.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Task thinking budget <span className="font-mono text-xs text-slate-400">(task_thinking_budget)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.config.task_thinking_budget ?? ''}
                    onChange={(e) =>
                      updateConfig(
                        'task_thinking_budget',
                        e.target.value === '' ? null : parseInt(e.target.value),
                      )
                    }
                    placeholder="기본 0. memory/summary/document 분석용"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Utility task용 thinking budget. 구조화 task는 0 권장.
                  </p>
                </div>
              </div>

              {/* Limits - Dynamic from schema */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Activity size={16} />
                  {t('tiers.limits')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      일일 채팅 제한 (daily_chat_limit, null=무제한)
                    </label>
                    <input
                      type="number"
                      value={formData.config.daily_chat_limit ?? ''}
                      onChange={(e) =>
                        updateConfig('daily_chat_limit', e.target.value === '' ? null : parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      일일 분석 제한 (daily_analysis_limit, null=무제한)
                    </label>
                    <input
                      type="number"
                      value={formData.config.daily_analysis_limit ?? ''}
                      onChange={(e) =>
                        updateConfig('daily_analysis_limit', e.target.value === '' ? null : parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">최대 문서 수 (max_documents)</label>
                    <input
                      type="number"
                      value={formData.config.max_documents || 0}
                      onChange={(e) => updateConfig('max_documents', parseInt(e.target.value) || 0)}
                      min={0}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  {(['max_storage_bytes', 'max_file_size_bytes'] as const).map((key) => {
                    const bytes = formData.config[key] || 0;
                    const unit = getByteUnit(key, bytes);
                    const displayVal = bytesToDisplay(bytes, unit);
                    return (
                      <div key={key}>
                        <label className="block text-xs text-slate-500 mb-1">
                          {key === 'max_storage_bytes'
                            ? '최대 저장용량 (max_storage_bytes)'
                            : '최대 파일 크기 (max_file_size_bytes)'}
                        </label>
                        <div className="flex gap-1">
                          <input
                            type="number"
                            value={displayVal}
                            onChange={(e) => updateConfig(key, displayToBytes(parseFloat(e.target.value) || 0, unit))}
                            min={0}
                            step={key === 'max_storage_bytes' ? 0.1 : 1}
                            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                          />
                          <select
                            value={unit}
                            onChange={(e) => {
                              const newUnit = e.target.value as 'MB' | 'GB';
                              setByteUnits({ ...byteUnits, [key]: newUnit });
                              updateConfig(key, displayToBytes(displayVal, newUnit));
                            }}
                            className="px-2 py-2 border border-slate-200 rounded-lg bg-white text-sm focus:outline-none focus:border-blue-500"
                          >
                            <option value="MB">MB</option>
                            <option value="GB">GB</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">최대 메모리 수 (max_memories)</label>
                    <input
                      type="number"
                      value={formData.config.max_memories || 0}
                      onChange={(e) => updateConfig('max_memories', parseInt(e.target.value) || 0)}
                      min={0}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      메모리 보관 기간 (memory_retention_days, null=영구)
                    </label>
                    <input
                      type="number"
                      value={formData.config.memory_retention_days ?? ''}
                      onChange={(e) =>
                        updateConfig('memory_retention_days', e.target.value === '' ? null : parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">최근 기억 주입 수 (memory_recent_count)</label>
                    <input
                      type="number"
                      value={formData.config.memory_recent_count || 0}
                      onChange={(e) => updateConfig('memory_recent_count', parseInt(e.target.value) || 0)}
                      min={0}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">검색 기억 주입 수 (memory_search_count)</label>
                    <input
                      type="number"
                      value={formData.config.memory_search_count || 0}
                      onChange={(e) => updateConfig('memory_search_count', parseInt(e.target.value) || 0)}
                      min={0}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      월간 멀티모달 제한 (monthly_multimodal_limit)
                    </label>
                    <input
                      type="number"
                      value={formData.config.monthly_multimodal_limit || 0}
                      onChange={(e) => updateConfig('monthly_multimodal_limit', parseInt(e.target.value) || 0)}
                      min={0}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      분석 시 기억 참조 수 (analysis_memory_count)
                    </label>
                    <input
                      type="number"
                      value={formData.config.analysis_memory_count || 0}
                      onChange={(e) => updateConfig('analysis_memory_count', parseInt(e.target.value) || 0)}
                      min={0}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Summary & Memory Trigger Settings */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <BookOpen size={16} />
                  대화 요약 / 기억 추출 트리거
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">최소 턴 수 (summary_min_turns)</label>
                    <input
                      type="number"
                      value={formData.config.summary_min_turns || 0}
                      onChange={(e) => updateConfig('summary_min_turns', parseInt(e.target.value) || 0)}
                      min={1}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">요약 후 유지 턴 수 (summary_keep_turns)</label>
                    <input
                      type="number"
                      value={formData.config.summary_keep_turns || 0}
                      onChange={(e) => updateConfig('summary_keep_turns', parseInt(e.target.value) || 0)}
                      min={1}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">
                      요약 최대 문장 수 (summary_max_sentences)
                    </label>
                    <input
                      type="number"
                      value={formData.config.summary_max_sentences || 0}
                      onChange={(e) => updateConfig('summary_max_sentences', parseInt(e.target.value) || 0)}
                      min={1}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">토큰 안전장치 (summary_max_tokens)</label>
                    <input
                      type="number"
                      value={formData.config.summary_max_tokens || 0}
                      onChange={(e) => updateConfig('summary_max_tokens', parseInt(e.target.value) || 0)}
                      min={1000}
                      step={1000}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">세션 유휴 시간 (session_idle_hours)</label>
                    <input
                      type="number"
                      value={formData.config.session_idle_hours || 0}
                      onChange={(e) => updateConfig('session_idle_hours', parseInt(e.target.value) || 0)}
                      min={1}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Schema Fields Info */}
              {schemaData && (
                <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-500">
                  <p className="font-medium text-slate-600 mb-2">사용 가능한 설정 필드:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {schemaData.fields.map((field) => (
                      <div key={field.key} title={field.description}>
                        <span className="font-mono text-slate-700">{field.key}</span>
                        <span className="text-slate-400"> ({field.type})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-1">Validation Error</p>
                  {validationErrors.map((err, i) => (
                    <p key={i} className="text-xs text-red-600">
                      {err}
                    </p>
                  ))}
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createTier.isPending || updateTier.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {(createTier.isPending || updateTier.isPending) && <Loader2 size={16} className="animate-spin" />}
                  {editingTier ? t('common.save') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tiers;
